export type LLMMatrikelEntry = {
   ejerlav: string;
   matnr: string;
   BFEnummer: string;
   adresse: string;
};

export type BuildLLMMatrikelDataContext = {
   /** Primary property / tingbog context (property info panel or one BFE in servitutredegørelse). */
   propertyData?: any;
   /** Additional BFE contexts when a servitut spans multiple properties. */
   propertyDataList?: any[];
   stamOplysninger?: {
      adresse?: {
         streetName?: string;
         buildingIdentifier?: string;
         postCode?: string;
         districtName?: string;
      };
   };
   xmlDoc?: Document | null;
};

function formatStamAdresse(stamOplysninger?: BuildLLMMatrikelDataContext["stamOplysninger"]): string {
   if (!stamOplysninger?.adresse) {
      return "";
   }
   const a = stamOplysninger.adresse;
   return `${a.streetName || ""} ${a.buildingIdentifier || ""}, ${a.postCode || ""} ${a.districtName || ""}`.trim();
}

function resolvePropertyAdresse(propertyData: any, stamOplysninger?: BuildLLMMatrikelDataContext["stamOplysninger"]): string {
   return (
      propertyData?.adresse
      || propertyData?.ViamapEjendomData?.ejdAdresse
      || formatStamAdresse(stamOplysninger)
      || ""
   );
}

function resolvePropertyBfe(propertyData: any): string {
   const fromGql = propertyData?.GQL_BFE?.MAT_SamletFastEjendom?.nodes?.find((n: any) => n.status === "Gældende")
      || propertyData?.GQL_BFE?.MAT_SamletFastEjendom?.nodes?.[0];
   return String(
      propertyData?.BFEnummer
      || fromGql?.BFEnummer
      || propertyData?.ViamapEjendomData?.sag?.BFEnr
      || propertyData?.Mat2?.samletfastejendomlokalid
      || ""
   );
}

function resolveEjerlavName(propertyData: any, ejerlavskode: number | string): string {
   if (ejerlavskode === undefined || ejerlavskode === null || ejerlavskode === "") {
      return "";
   }
   const codeStr = String(ejerlavskode).replace(/\s+/g, "").trim();
   const codeNum = parseInt(codeStr, 10);
   const ejerlavList = Array.isArray(propertyData?.Ejerlav)
      ? propertyData.Ejerlav
      : propertyData?.Ejerlav
         ? [propertyData.Ejerlav]
         : [];
   const fromList = ejerlavList.find(
      (e: any) => Number(e?.kode) === codeNum || String(e?.kode) === codeStr
   );
   if (typeof fromList?.navn === "string" && fromList.navn.trim()) {
      return fromList.navn.trim();
   }
   if (propertyData?.Ejerlav?.kode === codeNum || String(propertyData?.Ejerlav?.kode) === codeStr) {
      return propertyData.Ejerlav.navn || "";
   }
   if (propertyData?.BBRSFE && Array.isArray(propertyData.BBRSFE)) {
      for (const sfe of propertyData.BBRSFE) {
         const jordstykker = sfe.properties?.jordstykke;
         if (!Array.isArray(jordstykker)) {
            continue;
         }
         for (const jordstykke of jordstykker) {
            if (jordstykke.properties?.ejerlavskode === codeNum || String(jordstykke.properties?.ejerlavskode) === codeStr) {
               return jordstykke.properties.ejerlavsnavn || "";
            }
         }
      }
   }
   return "";
}

function normalizeMatnr(value: unknown): string {
   return value ? String(value).replace(/^0*/, "") : "";
}

function pushMatrikelEntry(
   target: LLMMatrikelEntry[],
   seen: Set<string>,
   entry: LLMMatrikelEntry
) {
   const matnr = normalizeMatnr(entry.matnr);
   const ejerlav = entry.ejerlav?.trim() || "";
   if (!matnr && !ejerlav) {
      return;
   }
   const key = `${entry.BFEnummer}|${ejerlav}|${matnr}`;
   if (seen.has(key)) {
      return;
   }
   seen.add(key);
   target.push({
      ejerlav,
      matnr,
      BFEnummer: entry.BFEnummer,
      adresse: entry.adresse,
   });
}

function collectFromGqlBfe(propertyData: any, stamOplysninger?: BuildLLMMatrikelDataContext["stamOplysninger"]): LLMMatrikelEntry[] {
   const entries: LLMMatrikelEntry[] = [];
   const sfe = propertyData?.GQL_BFE?.MAT_SamletFastEjendom?.nodes?.find((node: any) => node.status === "Gældende")
      || propertyData?.GQL_BFE?.MAT_SamletFastEjendom?.nodes?.[0];
   if (!sfe) {
      return entries;
   }
   const bfe = resolvePropertyBfe(propertyData);
   const adresse = resolvePropertyAdresse(propertyData, stamOplysninger);
   const jordstykker = (sfe.jordstykkeSamlesISamletFastEjendom?.nodes || [])
      .filter((j: any) => j.status === "Gældende")
      .filter((j: any) => !sfe.id_lokalId || j.samletFastEjendomLokalId === sfe.id_lokalId);

   for (const jordstykke of jordstykker) {
      const matnr = normalizeMatnr(jordstykke.matrikelnummer);
      const ejerlavskode = jordstykke.jordstykkeLiggerIEjerlav?.ejerlavskode;
      const ejerlav = jordstykke.jordstykkeLiggerIEjerlav?.ejerlavsnavn?.trim()
         || (ejerlavskode !== undefined ? resolveEjerlavName(propertyData, ejerlavskode) : "");
      if (matnr) {
         entries.push({ ejerlav, matnr, BFEnummer: bfe, adresse });
      }
   }
   return entries;
}

function collectFromSinglePropertyData(
   propertyData: any,
   stamOplysninger?: BuildLLMMatrikelDataContext["stamOplysninger"]
): LLMMatrikelEntry[] {
   const entries: LLMMatrikelEntry[] = [];
   const adresse = resolvePropertyAdresse(propertyData, stamOplysninger);

   if (propertyData?.BBRSFE && Array.isArray(propertyData.BBRSFE)) {
      for (const sfe of propertyData.BBRSFE) {
         const bfe = sfe.properties?.BFEnummer ? String(sfe.properties.BFEnummer) : resolvePropertyBfe(propertyData);
         const jordstykker = sfe.properties?.jordstykke;
         if (!Array.isArray(jordstykker)) {
            continue;
         }
         for (const jordstykke of jordstykker) {
            const matnr = normalizeMatnr(jordstykke.properties?.matrikelnummer);
            const ejerlavskode = jordstykke.properties?.ejerlavskode;
            const ejerlav = jordstykke.properties?.ejerlavsnavn?.trim()
               || (ejerlavskode !== undefined ? resolveEjerlavName(propertyData, ejerlavskode) : "");
            if (matnr) {
               entries.push({ ejerlav, matnr, BFEnummer: bfe, adresse });
            }
         }
      }
   }

   if (propertyData?.ViamapEjendomData?.sag?.matrikler && Array.isArray(propertyData.ViamapEjendomData.sag.matrikler)) {
      const bfe = String(propertyData.ViamapEjendomData.sag.BFEnr || resolvePropertyBfe(propertyData));
      const ejdAdresse = propertyData.ViamapEjendomData.ejdAdresse || adresse;
      for (const matrikel of propertyData.ViamapEjendomData.sag.matrikler) {
         const matnr = normalizeMatnr(matrikel.matrikelnummer);
         const ejerlavskode = matrikel.ejerlavskode;
         let ejerlav = matrikel.ejerlavsnavn?.trim() || "";
         if (!ejerlav && ejerlavskode !== undefined) {
            ejerlav = resolveEjerlavName(propertyData, ejerlavskode);
         }
         if (matnr) {
            entries.push({ ejerlav, matnr, BFEnummer: bfe, adresse: ejdAdresse });
         }
      }
   }

   if (propertyData?.Mat2) {
      const mat2 = propertyData.Mat2;
      const matnr = normalizeMatnr(mat2.matrikelnummer);
      const ejerlavskode = mat2.ejerlavskode;
      if (matnr) {
         entries.push({
            ejerlav: ejerlavskode !== undefined ? resolveEjerlavName(propertyData, ejerlavskode) : "",
            matnr,
            BFEnummer: String(mat2.samletfastejendomlokalid || resolvePropertyBfe(propertyData)),
            adresse,
         });
      }
   }

   entries.push(...collectFromGqlBfe(propertyData, stamOplysninger));

   return entries;
}

function collectFromXml(
   xmlDoc: Document,
   stamOplysninger?: BuildLLMMatrikelDataContext["stamOplysninger"]
): LLMMatrikelEntry[] {
   const entries: LLMMatrikelEntry[] = [];
   const stamAdresse = formatStamAdresse(stamOplysninger);
   const ejendomme = Array.from(xmlDoc.getElementsByTagName("FastEjendomMedRettigheder"));

   for (const ejendom of ejendomme) {
      const bfe = ejendom.getElementsByTagName("BestemtFastEjendomNummer")[0]?.textContent || "";
      const matrikler = Array.from(ejendom.getElementsByTagName("Matrikel"));

      for (const matrikel of matrikler) {
         const ejerlav = matrikel.getElementsByTagName("CadastralDistrictName")[0]?.textContent?.trim() || "";
         const matnr = normalizeMatnr(matrikel.getElementsByTagName("Matrikelnummer")[0]?.textContent);
         if (matnr || ejerlav) {
            entries.push({ ejerlav, matnr, BFEnummer: bfe, adresse: stamAdresse });
         }
      }
   }
   return entries;
}

/** Builds matrikelData for servitut AI metadata from all known property sources. */
export function buildLLMMatrikelData(ctx: BuildLLMMatrikelDataContext): LLMMatrikelEntry[] {
   const result: LLMMatrikelEntry[] = [];
   const seen = new Set<string>();

   const sources = [
      ...(ctx.propertyDataList?.length ? ctx.propertyDataList : []),
      ...(ctx.propertyData ? [ctx.propertyData] : []),
   ];
   const uniqueSources = sources.filter((source, index) => source && sources.indexOf(source) === index);

   for (const propertyData of uniqueSources) {
      for (const entry of collectFromSinglePropertyData(propertyData, ctx.stamOplysninger)) {
         pushMatrikelEntry(result, seen, entry);
      }
   }

   if (result.length === 0 && ctx.xmlDoc) {
      try {
         for (const entry of collectFromXml(ctx.xmlDoc, ctx.stamOplysninger)) {
            pushMatrikelEntry(result, seen, entry);
         }
      } catch {
      }
   }

   if (result.length === 0) {
      const fallbackSource = uniqueSources[0] ?? ctx.propertyData;
      const adresse = resolvePropertyAdresse(fallbackSource, ctx.stamOplysninger);
      const bfe = resolvePropertyBfe(fallbackSource);
      if (adresse || bfe) {
         pushMatrikelEntry(result, seen, {
            ejerlav: "",
            matnr: "",
            BFEnummer: bfe,
            adresse,
         });
      }
   }

   return result;
}
